import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Trophy, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { useGames } from "../context/GamesContext";

const MULTI_LABEL = "Multi-Title";

function fmtDateTime(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function TournamentDetail() {
  const { id } = useParams();
  const { labelOf } = useGames();
  const [t, setT] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/tournaments/${id}`)
      .then((r) => setT(r.data))
      .catch(() => setError("Tournament not found"));
  }, [id]);

  const matches = useMemo(() => {
    const arr = [...(t?.matches || [])];
    return arr.sort((a, b) => new Date(a.played_at || 0) - new Date(b.played_at || 0));
  }, [t]);

  if (error)
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-20 text-neutral-400" data-testid="tournament-detail-error">
        {error} — <Link to="/tournaments" className="text-axe-purple underline">back</Link>
      </div>
    );
  if (!t)
    return <div className="max-w-3xl mx-auto px-5 md:px-8 py-20 text-neutral-400" data-testid="tournament-detail-loading">Loading…</div>;

  return (
    <div className="text-white" data-testid="tournament-detail">
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-12 md:pt-16">
        <Link
          to="/tournaments"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-axe-purple hover:text-white"
        >
          <ArrowLeft size={14} /> All events
        </Link>

        <div className="mt-10 relative overflow-hidden rounded-3xl border border-axe-border bg-axe-surface">
          {t.image && (
            <div className="relative aspect-[21/9]">
              <img src={t.image} alt={t.name} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-axe-surface via-axe-surface/30 to-transparent" />
            </div>
          )}
          <div className="p-7 md:p-10">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.25em] font-bold ${
                t.status === "live" ? "bg-red-500 text-white animate-pulse"
                : t.status === "upcoming" ? "bg-axe-tangerine text-black"
                : "bg-axe-purple/80 text-white"
              }`}>{t.status}</span>
              <span className="px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.25em] font-bold bg-axe-bg border border-axe-border text-neutral-300">
                {t.game === "multi" ? MULTI_LABEL : labelOf(t.game)}
              </span>
            </div>
            <h1 className="mt-4 font-display font-black text-3xl sm:text-5xl leading-[1.03]">
              {t.name}
            </h1>
            <div className="mt-6 flex flex-wrap gap-5 text-neutral-400 text-sm">
              <span className="inline-flex items-center gap-2">
                <Calendar size={16} className="text-axe-tangerine" /> {fmtDateTime(t.date)}
              </span>
              {t.prize_pool && (
                <span className="inline-flex items-center gap-2">
                  <Trophy size={16} className="text-axe-tangerine" /> {t.prize_pool}
                </span>
              )}
              {t.result && (
                <span className="inline-flex items-center gap-2 text-axe-tangerine font-semibold">
                  {t.result}
                </span>
              )}
            </div>
            {t.registration_url && t.registration_url !== "#" && t.status !== "completed" && (
              <a
                href={t.registration_url}
                target="_blank"
                rel="noreferrer"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-axe-purple px-7 py-3 text-xs uppercase tracking-[0.25em] font-bold text-white hover:bg-axe-purple-hover"
                data-testid="tournament-detail-register"
              >
                Register <ExternalLink size={14} />
              </a>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-14">
        <div className="text-xs uppercase tracking-[0.35em] text-axe-tangerine font-bold">
          Matches
        </div>
        <h2 className="mt-3 font-display font-bold text-2xl sm:text-3xl">Results & Schedule</h2>

        <div className="mt-8">
          {matches.length === 0 ? (
            <div className="rounded-2xl border border-axe-border bg-axe-surface p-8 text-center text-neutral-500" data-testid="tournament-no-matches">
              No matches posted yet. Check back as the bracket fills in.
            </div>
          ) : (
            <div className="space-y-3">
              {matches.map((m) => (
                <div
                  key={m.id}
                  data-testid={`match-${m.id}`}
                  className="rounded-2xl border border-axe-border bg-axe-surface p-5 flex flex-wrap items-center gap-5"
                >
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold w-24">
                    <span className={`inline-block px-3 py-1 rounded-full ${
                      m.status === "live" ? "bg-red-500 text-white animate-pulse"
                      : m.status === "completed" ? "bg-axe-purple/80 text-white"
                      : "bg-axe-tangerine/20 text-axe-tangerine border border-axe-tangerine/40"
                    }`}>{m.status}</span>
                  </div>
                  <div className="flex items-center gap-6 flex-1 min-w-[240px]">
                    <div className="flex-1 text-right">
                      <div className="font-display font-bold text-lg">{m.team_a}</div>
                    </div>
                    <div className="font-display font-black text-2xl tabular-nums">
                      <span className={m.score_a > m.score_b ? "text-axe-tangerine" : "text-neutral-400"}>
                        {m.score_a ?? "—"}
                      </span>
                      <span className="mx-2 text-neutral-600">vs</span>
                      <span className={m.score_b > m.score_a ? "text-axe-tangerine" : "text-neutral-400"}>
                        {m.score_b ?? "—"}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="font-display font-bold text-lg">{m.team_b}</div>
                    </div>
                  </div>
                  {(m.played_at || m.notes) && (
                    <div className="text-xs text-neutral-500 w-full pt-2 border-t border-axe-border flex flex-wrap gap-4">
                      {m.played_at && <span>{fmtDateTime(m.played_at)}</span>}
                      {m.notes && <span>· {m.notes}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
